import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  Phone,
  PhoneCall,
  PhoneOff,
  PhoneMissed,
  PhoneForwarded,
  Clock,
  Activity,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Zap,
  Users,
  TrendingUp,
  RefreshCw,
  ArrowRight,
  Voicemail,
  ExternalLink,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  MessageSquare,
  X,
} from 'lucide-react'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Badge,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui'
import { engagementApi, type RecentCall, type RecentLead } from '@/api/endpoints/engagement'
import { cn } from '@/lib/utils'

// Sorting types
type SortField = 'created_at' | 'duration_seconds' | 'turn_count' | 'company_name' | 'answered_by' | 'status' | 'sentiment'
type SortOrder = 'asc' | 'desc'

// Filters type
interface CallFilters {
  status: string
  answeredBy: string
  sentiment: string
  transferred: string
}

// Format relative time (handles UTC timestamps from server)
const formatRelativeTime = (dateStr: string | null) => {
  if (!dateStr) return 'N/A'
  // Ensure UTC parsing by appending Z if not present
  const normalizedDate = dateStr.endsWith('Z') ? dateStr : dateStr + 'Z'
  const date = new Date(normalizedDate)
  const now = new Date()
  const diff = now.getTime() - date.getTime()

  // Handle edge case where diff is negative (clock skew)
  if (diff < 0) return 'just now'

  const seconds = Math.floor(diff / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)

  if (seconds < 60) return `${seconds}s ago`
  if (minutes < 60) return `${minutes}m ago`
  if (hours < 24) return `${hours}h ago`
  return date.toLocaleDateString()
}

// Format duration
const formatDuration = (seconds: number | null) => {
  if (seconds === null || seconds === undefined) return '--'
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

// Get status badge color
const getStatusColor = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'completed':
      return 'bg-green-100 text-green-800'
    case 'in-progress':
    case 'ringing':
      return 'bg-blue-100 text-blue-800'
    case 'failed':
    case 'error':
      return 'bg-red-100 text-red-800'
    case 'queued':
      return 'bg-yellow-100 text-yellow-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

// Get answered by icon
const getAnsweredByIcon = (answeredBy: string | null) => {
  switch (answeredBy?.toLowerCase()) {
    case 'human':
      return <Users className="h-4 w-4 text-green-600" />
    case 'voicemail':
      return <Voicemail className="h-4 w-4 text-orange-500" />
    case 'no-answer':
      return <PhoneMissed className="h-4 w-4 text-red-500" />
    default:
      return <Phone className="h-4 w-4 text-gray-400" />
  }
}

// Get sentiment badge color
const getSentimentColor = (sentiment: string | null) => {
  switch (sentiment?.toLowerCase()) {
    case 'positive':
      return 'bg-green-100 text-green-800'
    case 'negative':
      return 'bg-red-100 text-red-800'
    case 'neutral':
      return 'bg-gray-100 text-gray-800'
    default:
      return 'bg-gray-50 text-gray-500'
  }
}

// Get turn count color (higher = better engagement)
const getTurnCountColor = (turnCount: number | null) => {
  if (turnCount === null) return 'text-gray-400'
  if (turnCount >= 10) return 'text-green-600 font-semibold'
  if (turnCount >= 5) return 'text-blue-600'
  if (turnCount >= 2) return 'text-gray-600'
  return 'text-gray-400'
}

// Circuit breaker state badge
const CircuitBreakerBadge = ({ state }: { state?: string }) => {
  switch (state) {
    case 'closed':
      return (
        <Badge className="bg-green-100 text-green-800">
          <CheckCircle2 className="h-3 w-3 mr-1" />
          Closed (OK)
        </Badge>
      )
    case 'open':
      return (
        <Badge className="bg-red-100 text-red-800">
          <XCircle className="h-3 w-3 mr-1" />
          Open (Blocking)
        </Badge>
      )
    case 'half_open':
    case 'half-open':
      return (
        <Badge className="bg-yellow-100 text-yellow-800">
          <AlertTriangle className="h-3 w-3 mr-1" />
          Half-Open (Testing)
        </Badge>
      )
    default:
      return (
        <Badge variant="secondary">
          Unknown
        </Badge>
      )
  }
}

export default function EngagementBatch() {
  const [autoRefresh, setAutoRefresh] = useState(true)
  const [timeRange, setTimeRange] = useState<number>(1)
  const [callLimit, setCallLimit] = useState<number>(50)

  // Sorting state for recent calls
  const [sortField, setSortField] = useState<SortField>('created_at')
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc')

  // Filters state for recent calls
  const [filters, setFilters] = useState<CallFilters>({
    status: 'all',
    answeredBy: 'all',
    sentiment: 'all',
    transferred: 'all',
  })

  // Fetch batch queue data with auto-refresh
  const { data: queueData, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['engagement-batch-queue', timeRange, callLimit],
    queryFn: () => engagementApi.getBatchQueue(callLimit, timeRange).then(res => res.data),
    staleTime: 5000,
    refetchInterval: autoRefresh ? 5000 : false,
  })

  // Fetch activity stream
  const { data: activityData } = useQuery({
    queryKey: ['engagement-activity-stream'],
    queryFn: () => engagementApi.getActivityStream(30).then(res => res.data),
    staleTime: 5000,
    refetchInterval: autoRefresh ? 10000 : false,
  })

  const stats = queueData?.stats
  const rateLimiter = queueData?.rate_limiter
  const circuitBreaker = queueData?.circuit_breaker
  const recentCalls = queueData?.recent_calls || []
  const recentLeads = queueData?.recent_leads || []

  // Handle sort click
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortOrder('desc')
    }
  }

  // Get sort icon for column header
  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-3 w-3 ml-1 opacity-50" />
    }
    return sortOrder === 'asc'
      ? <ArrowUp className="h-3 w-3 ml-1" />
      : <ArrowDown className="h-3 w-3 ml-1" />
  }

  // Check if any filters are active
  const hasActiveFilters = filters.status !== 'all' || filters.answeredBy !== 'all' || filters.sentiment !== 'all' || filters.transferred !== 'all'

  // Clear all filters
  const clearFilters = () => {
    setFilters({ status: 'all', answeredBy: 'all', sentiment: 'all', transferred: 'all' })
  }

  // Filter and sort recent calls
  const filteredAndSortedCalls = useMemo(() => {
    let result = [...recentCalls]

    // Apply filters
    if (filters.status !== 'all') {
      result = result.filter(call => call.status?.toLowerCase() === filters.status)
    }
    if (filters.answeredBy !== 'all') {
      result = result.filter(call => call.answered_by?.toLowerCase() === filters.answeredBy)
    }
    if (filters.sentiment !== 'all') {
      result = result.filter(call => call.sentiment?.toLowerCase() === filters.sentiment)
    }
    if (filters.transferred !== 'all') {
      const showTransferred = filters.transferred === 'yes'
      result = result.filter(call => call.transferred === showTransferred)
    }

    // Apply sorting
    result.sort((a, b) => {
      let aVal: string | number | null = null
      let bVal: string | number | null = null

      switch (sortField) {
        case 'created_at':
          aVal = a.created_at || ''
          bVal = b.created_at || ''
          break
        case 'duration_seconds':
          aVal = a.duration_seconds ?? -1
          bVal = b.duration_seconds ?? -1
          break
        case 'turn_count':
          aVal = a.turn_count ?? -1
          bVal = b.turn_count ?? -1
          break
        case 'company_name':
          aVal = a.company_name?.toLowerCase() || ''
          bVal = b.company_name?.toLowerCase() || ''
          break
        case 'answered_by':
          aVal = a.answered_by || ''
          bVal = b.answered_by || ''
          break
        case 'status':
          aVal = a.status || ''
          bVal = b.status || ''
          break
        case 'sentiment':
          aVal = a.sentiment || ''
          bVal = b.sentiment || ''
          break
      }

      if (aVal === null && bVal === null) return 0
      if (aVal === null) return 1
      if (bVal === null) return -1

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortOrder === 'asc' ? aVal - bVal : bVal - aVal
      }

      const comparison = String(aVal).localeCompare(String(bVal))
      return sortOrder === 'asc' ? comparison : -comparison
    })

    return result
  }, [recentCalls, filters, sortField, sortOrder])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Engagement Batch Monitor</h1>
          <p className="text-muted-foreground">
            Real-time view of the engagement queue processing
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={cn(autoRefresh && 'border-green-500 text-green-600')}
          >
            <RefreshCw className={cn('h-4 w-4 mr-2', autoRefresh && 'animate-spin')} />
            {autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCw className={cn('h-4 w-4', isFetching && 'animate-spin')} />
          </Button>
        </div>
      </div>

      {/* System Status Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Today's Calls */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Today's Calls</p>
                <p className="text-3xl font-bold">{stats?.today?.total_calls || 0}</p>
              </div>
              <PhoneCall className="h-8 w-8 text-blue-500" />
            </div>
            <div className="mt-2 flex gap-2 text-xs">
              <span className="text-green-600">{stats?.today?.completed || 0} completed</span>
              <span className="text-red-600">{stats?.today?.failed || 0} failed</span>
            </div>
          </CardContent>
        </Card>

        {/* Human Answered */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Human Answered</p>
                <p className="text-3xl font-bold">{stats?.today?.human_answered || 0}</p>
              </div>
              <Users className="h-8 w-8 text-green-500" />
            </div>
            <div className="mt-2 text-xs text-muted-foreground">
              {stats?.today?.total_calls
                ? `${Math.round((stats.today.human_answered / stats.today.total_calls) * 100)}% answer rate`
                : 'No calls yet'}
            </div>
          </CardContent>
        </Card>

        {/* Voicemail */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Voicemail</p>
                <p className="text-3xl font-bold">{stats?.today?.voicemail || 0}</p>
              </div>
              <Voicemail className="h-8 w-8 text-orange-500" />
            </div>
            <div className="mt-2 text-xs text-muted-foreground">
              {stats?.today?.total_calls
                ? `${Math.round((stats.today.voicemail / stats.today.total_calls) * 100)}% of calls`
                : 'No calls yet'}
            </div>
          </CardContent>
        </Card>

        {/* Last Hour Activity */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Last Hour</p>
                <p className="text-3xl font-bold">{stats?.last_hour?.calls_made || 0}</p>
              </div>
              <Clock className="h-8 w-8 text-purple-500" />
            </div>
            <div className="mt-2 text-xs text-muted-foreground">
              {stats?.last_hour?.leads_engaged || 0} leads engaged
            </div>
          </CardContent>
        </Card>
      </div>

      {/* System Health */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Rate Limiter Status */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Zap className="h-4 w-4" />
              Rate Limiter
            </CardTitle>
          </CardHeader>
          <CardContent>
            {rateLimiter?.error ? (
              <p className="text-sm text-muted-foreground">{rateLimiter.error}</p>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Tokens</span>
                  <span className="font-medium text-sm">{rateLimiter?.tokens?.toFixed(1) || 'N/A'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">Rate</span>
                  <span className="font-medium text-sm">{rateLimiter?.calls_per_second || 0.25}/sec</span>
                </div>
                {/* Progress bar for tokens */}
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div
                    className="bg-blue-600 h-1.5 rounded-full transition-all"
                    style={{ width: `${Math.min(100, (rateLimiter?.tokens || 0) * 10)}%` }}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Circuit Breaker Status */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Activity className="h-4 w-4" />
              Circuit Breaker (Bland.ai)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {circuitBreaker?.error ? (
              <p className="text-sm text-muted-foreground">{circuitBreaker.error}</p>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">State</span>
                  <CircuitBreakerBadge state={circuitBreaker?.state} />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Failure Count</span>
                  <span className={cn(
                    'font-medium',
                    (circuitBreaker?.failure_count || 0) > 3 && 'text-red-600'
                  )}>
                    {circuitBreaker?.failure_count || 0}
                  </span>
                </div>
                {circuitBreaker?.state === 'open' && circuitBreaker?.retry_after && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Retry After</span>
                    <span className="font-medium text-orange-600">
                      {circuitBreaker.retry_after.toFixed(0)}s
                    </span>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Today's Lead Sources */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Today's Leads by Source
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(() => {
              const sources = stats?.leads_by_source || {}
              const entries = Object.entries(sources)
                .filter(([, count]) => count > 0)
                .sort((a, b) => b[1] - a[1])
              const total = entries.reduce((sum, [, count]) => sum + count, 0)

              const sourceColors: Record<string, string> = {
                riviera_applied: 'bg-blue-500',
                riviera_approved: 'bg-green-500',
                approved_30_days: 'bg-emerald-400',
                approved_60_days: 'bg-emerald-500',
                approved_90_days: 'bg-emerald-600',
                approved_180_days: 'bg-emerald-700',
                applied_30_days: 'bg-sky-400',
                applied_60_days: 'bg-sky-500',
                applied_90_days: 'bg-sky-600',
                applied_180_days: 'bg-sky-700',
                manual: 'bg-purple-500',
              }

              const sourceLabels: Record<string, string> = {
                riviera_applied: 'Applied',
                riviera_approved: 'Approved',
                approved_30_days: 'Approved 30d',
                approved_60_days: 'Approved 60d',
                approved_90_days: 'Approved 90d',
                approved_180_days: 'Approved 180d',
                applied_30_days: 'Applied 30d',
                applied_60_days: 'Applied 60d',
                applied_90_days: 'Applied 90d',
                applied_180_days: 'Applied 180d',
                manual: 'Manual',
              }

              if (total === 0) {
                return <span className="text-xs text-muted-foreground">No leads today</span>
              }

              return (
                <div className="space-y-3">
                  {/* Stacked bar */}
                  <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden flex">
                    {entries.map(([source, count]) => (
                      <div
                        key={source}
                        className={`${sourceColors[source] || 'bg-gray-400'} h-full transition-all`}
                        style={{ width: `${Math.round((count / total) * 100)}%` }}
                        title={`${sourceLabels[source] || source}: ${count}`}
                      />
                    ))}
                  </div>
                  {/* Legend */}
                  <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs">
                    {entries.map(([source, count]) => (
                      <div key={source} className="flex items-center gap-1">
                        <div className={`w-2 h-2 rounded-full ${sourceColors[source] || 'bg-gray-400'}`} />
                        <span className="text-muted-foreground">{sourceLabels[source] || source}</span>
                        <span className="font-semibold">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })()}
          </CardContent>
        </Card>

        {/* This Week's Lead Sources */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Week's Sources
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {(() => {
              const sources = stats?.leads_by_source_week || {}
              const entries = Object.entries(sources)
                .filter(([, count]) => count > 0)
                .sort((a, b) => b[1] - a[1])
              const total = entries.reduce((sum, [, count]) => sum + count, 0)

              const sourceColors: Record<string, string> = {
                riviera_applied: 'bg-blue-500',
                riviera_approved: 'bg-green-500',
                approved_30_days: 'bg-emerald-400',
                approved_60_days: 'bg-emerald-500',
                approved_90_days: 'bg-emerald-600',
                approved_180_days: 'bg-emerald-700',
                applied_30_days: 'bg-sky-400',
                applied_60_days: 'bg-sky-500',
                applied_90_days: 'bg-sky-600',
                applied_180_days: 'bg-sky-700',
                manual: 'bg-purple-500',
              }

              const sourceLabels: Record<string, string> = {
                riviera_applied: 'Applied',
                riviera_approved: 'Approved',
                approved_30_days: 'Appr 30d',
                approved_60_days: 'Appr 60d',
                approved_90_days: 'Appr 90d',
                approved_180_days: 'Appr 180d',
                applied_30_days: 'Appl 30d',
                applied_60_days: 'Appl 60d',
                applied_90_days: 'Appl 90d',
                applied_180_days: 'Appl 180d',
                manual: 'Manual',
              }

              if (total === 0) {
                return <span className="text-xs text-muted-foreground">No leads this week</span>
              }

              return (
                <div className="space-y-1.5">
                  {entries.map(([source, count]) => (
                    <div key={source} className="flex items-center gap-2 text-xs">
                      <div className={`w-2 h-2 rounded-full shrink-0 ${sourceColors[source] || 'bg-gray-400'}`} />
                      <span className="text-muted-foreground truncate">{sourceLabels[source] || source}</span>
                      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${sourceColors[source] || 'bg-gray-400'}`}
                          style={{ width: `${Math.round((count / entries[0][1]) * 100)}%` }}
                        />
                      </div>
                      <span className="font-semibold tabular-nums">{count}</span>
                    </div>
                  ))}
                  <div className="flex items-center justify-between pt-1 border-t text-xs">
                    <span className="text-muted-foreground">Total</span>
                    <span className="font-bold">{total}</span>
                  </div>
                </div>
              )
            })()}
          </CardContent>
        </Card>
      </div>

      {/* Activity Summary */}
      {activityData && activityData.by_type && Object.keys(activityData.by_type).length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Activity Summary (Last {activityData.period_minutes} min)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {Object.entries(activityData.by_type).map(([type, count]) => (
                <Badge key={type} variant="secondary" className="text-sm">
                  {type.replace(/_/g, ' ')}: {count}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Calls Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <PhoneCall className="h-5 w-5" />
                Recent Calls
                <Select
                  value={String(timeRange)}
                  onValueChange={(value) => setTimeRange(Number(value))}
                >
                  <SelectTrigger className="w-[140px] h-7 text-xs font-normal">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Last Hour</SelectItem>
                    <SelectItem value="2">Last 2 Hours</SelectItem>
                    <SelectItem value="4">Last 4 Hours</SelectItem>
                    <SelectItem value="8">Last 8 Hours</SelectItem>
                    <SelectItem value="12">Last 12 Hours</SelectItem>
                    <SelectItem value="24">Last 24 Hours</SelectItem>
                  </SelectContent>
                </Select>
              </CardTitle>
              <CardDescription>
                {filteredAndSortedCalls.length} of {recentCalls.length} calls
                {hasActiveFilters && ' (filtered)'}
              </CardDescription>
            </div>
            {/* Filters */}
            <div className="flex items-center gap-2">
              <Select
                value={filters.status}
                onValueChange={(value) => setFilters(f => ({ ...f, status: value }))}
              >
                <SelectTrigger className="w-[130px] h-8 text-xs">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="in-progress">In Progress</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={filters.answeredBy}
                onValueChange={(value) => setFilters(f => ({ ...f, answeredBy: value }))}
              >
                <SelectTrigger className="w-[130px] h-8 text-xs">
                  <SelectValue placeholder="Answered By" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Answers</SelectItem>
                  <SelectItem value="human">Human</SelectItem>
                  <SelectItem value="voicemail">Voicemail</SelectItem>
                  <SelectItem value="no-answer">No Answer</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={filters.sentiment}
                onValueChange={(value) => setFilters(f => ({ ...f, sentiment: value }))}
              >
                <SelectTrigger className="w-[130px] h-8 text-xs">
                  <SelectValue placeholder="Sentiment" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sentiment</SelectItem>
                  <SelectItem value="positive">Positive</SelectItem>
                  <SelectItem value="neutral">Neutral</SelectItem>
                  <SelectItem value="negative">Negative</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={filters.transferred}
                onValueChange={(value) => setFilters(f => ({ ...f, transferred: value }))}
              >
                <SelectTrigger className="w-[130px] h-8 text-xs">
                  <SelectValue placeholder="Transferred" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Calls</SelectItem>
                  <SelectItem value="yes">Transferred</SelectItem>
                  <SelectItem value="no">Not Transferred</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={callLimit.toString()}
                onValueChange={(value) => setCallLimit(parseInt(value))}
              >
                <SelectTrigger className="w-[100px] h-8 text-xs">
                  <SelectValue placeholder="Limit" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="25">25 calls</SelectItem>
                  <SelectItem value="50">50 calls</SelectItem>
                  <SelectItem value="100">100 calls</SelectItem>
                  <SelectItem value="200">200 calls</SelectItem>
                </SelectContent>
              </Select>
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="h-8 px-2">
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : filteredAndSortedCalls.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <PhoneOff className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{hasActiveFilters ? 'No calls match your filters' : 'No calls in the last hour'}</p>
              {hasActiveFilters && (
                <Button variant="link" onClick={clearFilters} className="mt-2">
                  Clear filters
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b text-left text-sm text-muted-foreground">
                    <th
                      className="pb-3 font-medium cursor-pointer hover:text-foreground"
                      onClick={() => handleSort('company_name')}
                    >
                      <div className="flex items-center">
                        Lead
                        {getSortIcon('company_name')}
                      </div>
                    </th>
                    <th className="pb-3 font-medium">Phone</th>
                    <th
                      className="pb-3 font-medium cursor-pointer hover:text-foreground"
                      onClick={() => handleSort('status')}
                    >
                      <div className="flex items-center">
                        Status
                        {getSortIcon('status')}
                      </div>
                    </th>
                    <th
                      className="pb-3 font-medium cursor-pointer hover:text-foreground"
                      onClick={() => handleSort('answered_by')}
                    >
                      <div className="flex items-center">
                        Answered By
                        {getSortIcon('answered_by')}
                      </div>
                    </th>
                    <th
                      className="pb-3 font-medium cursor-pointer hover:text-foreground"
                      onClick={() => handleSort('duration_seconds')}
                    >
                      <div className="flex items-center">
                        Duration
                        {getSortIcon('duration_seconds')}
                      </div>
                    </th>
                    <th
                      className="pb-3 font-medium cursor-pointer hover:text-foreground"
                      onClick={() => handleSort('turn_count')}
                    >
                      <div className="flex items-center">
                        <MessageSquare className="h-3 w-3 mr-1" />
                        Turns
                        {getSortIcon('turn_count')}
                      </div>
                    </th>
                    <th
                      className="pb-3 font-medium cursor-pointer hover:text-foreground"
                      onClick={() => handleSort('sentiment')}
                    >
                      <div className="flex items-center">
                        Sentiment
                        {getSortIcon('sentiment')}
                      </div>
                    </th>
                    <th
                      className="pb-3 font-medium cursor-pointer hover:text-foreground"
                      onClick={() => handleSort('created_at')}
                    >
                      <div className="flex items-center">
                        Time
                        {getSortIcon('created_at')}
                      </div>
                    </th>
                    <th className="pb-3 font-medium"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredAndSortedCalls.map((call: RecentCall) => (
                    <tr
                      key={call.id}
                      className="text-sm hover:bg-muted/50 group/row relative"
                    >
                      <td className="py-3">
                        <Link
                          to={`/leads?search=${call.lead_id}`}
                          className="text-blue-600 hover:underline font-medium"
                        >
                          #{call.lead_id}
                        </Link>
                        {call.company_name && (
                          <div className="text-xs text-muted-foreground truncate max-w-[150px]">
                            {call.company_name}
                          </div>
                        )}
                      </td>
                      <td className="py-3 font-mono text-sm">
                        {call.phone || '--'}
                      </td>
                      <td className="py-3">
                        <Badge className={getStatusColor(call.status)}>
                          {call.status}
                        </Badge>
                      </td>
                      <td className="py-3">
                        <div className="flex items-center gap-2">
                          {getAnsweredByIcon(call.answered_by)}
                          <span className="capitalize">{call.answered_by || 'Unknown'}</span>
                          {call.transferred && (
                            <Badge className="bg-purple-100 text-purple-800 ml-1">
                              <PhoneForwarded className="h-3 w-3 mr-1" />
                              Transferred
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="py-3 font-mono">
                        {formatDuration(call.duration_seconds)}
                      </td>
                      <td className="py-3">
                        <span className={getTurnCountColor(call.turn_count)}>
                          {call.turn_count !== null ? call.turn_count : '--'}
                        </span>
                      </td>
                      <td className="py-3">
                        {call.sentiment ? (
                          <Badge className={getSentimentColor(call.sentiment)}>
                            {call.sentiment}
                          </Badge>
                        ) : (
                          <span className="text-gray-400">--</span>
                        )}
                      </td>
                      <td className="py-3 text-muted-foreground">
                        {formatRelativeTime(call.created_at)}
                      </td>
                      <td className="py-3">
                        {call.summary_id ? (
                          <Link
                            to={`/conversations?type=call&id=${call.summary_id}`}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Link>
                        ) : (
                          <span className="text-gray-300">
                            <ExternalLink className="h-4 w-4" />
                          </span>
                        )}
                      </td>
                      {/* Transcript preview tooltip */}
                      {call.transcript_preview && (
                        <td className="p-0 border-0 w-0 overflow-visible">
                          <div className="hidden group-hover/row:block absolute left-4 top-full z-50 w-[400px] max-h-[200px] overflow-y-auto bg-popover border rounded-md shadow-lg p-3 text-xs whitespace-pre-wrap text-popover-foreground">
                            <div className="font-semibold text-xs text-muted-foreground mb-1 uppercase tracking-wide">Transcript Preview</div>
                            {call.transcript_preview}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recently Engaged Leads */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Recently Engaged Leads
          </CardTitle>
          <CardDescription>
            {recentLeads.length} leads engaged in the last hour
            {recentLeads.filter(l => (l.turn_count ?? 0) >= 5).length > 0 && (
              <span className="ml-2 text-green-600">
                ({recentLeads.filter(l => (l.turn_count ?? 0) >= 5).length} quality engagements)
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : recentLeads.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No leads engaged in the last hour</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b text-left text-sm text-muted-foreground">
                    <th className="pb-3 font-medium">Company</th>
                    <th className="pb-3 font-medium">Phone</th>
                    <th className="pb-3 font-medium">Status</th>
                    <th className="pb-3 font-medium">Attempts</th>
                    <th className="pb-3 font-medium">
                      <div className="flex items-center">
                        <MessageSquare className="h-3 w-3 mr-1" />
                        Turns
                      </div>
                    </th>
                    <th className="pb-3 font-medium">Duration</th>
                    <th className="pb-3 font-medium">Sentiment</th>
                    <th className="pb-3 font-medium">Last Contact</th>
                    <th className="pb-3 font-medium"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {recentLeads.map((lead: RecentLead) => (
                    <tr
                      key={lead.id}
                      className={cn(
                        "text-sm hover:bg-muted/50",
                        // Highlight quality engagements (5+ turns)
                        (lead.turn_count ?? 0) >= 5 && "bg-green-50/50"
                      )}
                    >
                      <td className="py-3 font-medium">{lead.company_name}</td>
                      <td className="py-3 font-mono text-muted-foreground">
                        {lead.phone}
                      </td>
                      <td className="py-3">
                        <Badge variant="secondary">{lead.status}</Badge>
                      </td>
                      <td className="py-3">{lead.call_attempt_count || 0}</td>
                      <td className="py-3">
                        <span className={getTurnCountColor(lead.turn_count)}>
                          {lead.turn_count !== null ? lead.turn_count : '--'}
                        </span>
                      </td>
                      <td className="py-3 font-mono">
                        {formatDuration(lead.last_call_duration)}
                      </td>
                      <td className="py-3">
                        {lead.sentiment ? (
                          <Badge className={getSentimentColor(lead.sentiment)}>
                            {lead.sentiment}
                          </Badge>
                        ) : (
                          <span className="text-gray-400">--</span>
                        )}
                      </td>
                      <td className="py-3 text-muted-foreground">
                        {formatRelativeTime(lead.last_contact_date)}
                      </td>
                      <td className="py-3">
                        <Link
                          to={`/leads?search=${lead.id}`}
                          className="inline-flex items-center text-blue-600 hover:text-blue-800"
                        >
                          View
                          <ArrowRight className="h-3 w-3 ml-1" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
